/**
 * stripe_c7.js — Stripe.js v3 card tokenization for Commerce7 club-signup
 * payment ONLY (common feature — any C7 tenant that configures a key).
 *
 * WHY: Commerce7's "add card to customer" endpoint (needed so a brand-new
 * card can be saved and later charged by id — the club-signup payment step
 * only ever charges via customerCreditCardId, never a raw card number)
 * requires a real tokenized card (tokenOnFile), not raw card fields. Cart
 * checkout's regular tender endpoint is different — it DOES accept raw card
 * fields for a one-time charge — so cart checkout has its own separate path
 * (features/optional/checkout_c7.js / variant CheckoutForm.js) and never
 * touches this module.
 *
 * Ported from the old Valle project's static/javascript/services/stripe.js
 * (initStripeElements/createStripeToken), minus the DOM/event wiring (that
 * lives in ClubCheckoutForm.js) and the hardcoded key (now per-tenant via
 * core/configAccessors.js's getCommerce7Config — each Commerce7 winery has
 * its own Stripe account).
 *
 * Publishable (not secret) key — safe to ship in the widget bundle/tenant
 * config; no Stripe secret key is ever involved in this flow.
 *
 * EXPORTS:
 *   loadStripeJs() — injects the Stripe.js v3 script once, resolves when ready.
 *   createCardElements({ numberEl, expiryEl, cvcEl }) — mounts Stripe Elements
 *     into the three given containers; returns { stripeInstance, cardNumberElement }.
 *   tokenizeCard(stripeInstance, cardNumberElement, cardHolderName) — returns
 *     { token, error }.
 */

import { getCommerce7Config } from "../../core/configAccessors.js";
import { error as logError } from "../../core/env.js";

let stripeJsPromise = null;

export function loadStripeJs() {
  if (typeof window.Stripe !== "undefined") return Promise.resolve();
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.onload = () => resolve();
    s.onerror = () => {
      stripeJsPromise = null;
      reject(new Error("Stripe.js failed to load"));
    };
    document.head.appendChild(s);
  });
  return stripeJsPromise;
}

// Same visual metrics as the old service's elementStyles, matched to
// .ws-checkout-input (ChatPanel.css) so the mounted iframes read as one
// system with the rest of the form.
const ELEMENT_STYLE = {
  style: {
    base: {
      color: "#000000",
      fontFamily: "Open Sans, sans-serif",
      fontSize: "14px",
      lineHeight: "20px",
      "::placeholder": { color: "#737373" },
    },
    invalid: {
      color: "#c84e4e",
      iconColor: "#c84e4e",
    },
  },
};

// containers: { numberEl, expiryEl, cvcEl } — DOM nodes already attached
// (Stripe Elements mount fine inside a Shadow DOM as long as the node is
// connected). Throws if no publishable key is configured for this tenant —
// callers should check getCommerce7Config first and hide the "+ Add new
// card" option entirely when it's empty, same as before this key existed.
export function createCardElements({ numberEl, expiryEl, cvcEl }) {
  const publishableKey = getCommerce7Config("stripe_publishable_key");
  if (!publishableKey) {
    throw new Error("No Stripe publishable key configured for this tenant");
  }
  const stripeInstance = window.Stripe(publishableKey);
  const elements = stripeInstance.elements();

  const cardNumberElement = elements.create("cardNumber", ELEMENT_STYLE);
  const cardExpiryElement = elements.create("cardExpiry", ELEMENT_STYLE);
  const cardCvcElement = elements.create("cardCvc", ELEMENT_STYLE);

  cardNumberElement.mount(numberEl);
  cardExpiryElement.mount(expiryEl);
  cardCvcElement.mount(cvcEl);

  return { stripeInstance, cardNumberElement, cardExpiryElement, cardCvcElement };
}

export async function tokenizeCard(stripeInstance, cardNumberElement, cardHolderName) {
  try {
    return await stripeInstance.createToken(cardNumberElement, { name: cardHolderName });
  } catch (e) {
    logError("[stripe_c7] tokenization failed", e);
    return { token: null, error: { message: "Card tokenization failed. Please try again." } };
  }
}
